import { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import {
    ComposableMap,
    Geographies,
    Geography,
    Marker,
} from 'react-simple-maps';
import ReactTooltip from 'react-tooltip';

import { projectionConfig } from 'constants/map/config';
import { useMeta } from 'hooks/api/useMeta';
import { useMapAutoScale, useMapData } from 'hooks/map';
import { useGallery } from 'hooks/store/useGallery';

import { MarkerIcon } from './MarkerIcon';
import { InteractiveMapProps } from './types';

export const InteractiveMap = (props?: InteractiveMapProps): JSX.Element => {
    const { svgRef, viewBox, scaleFactor } = useMapAutoScale();

    const { groups, hidden, states, markers, height, markerScale, ...rest } =
        props || {};

    const { data } = useMapData({
        groups,
        hidden,
        states,
    });

    const [tooltipContent, setTooltipContent] = useState('');

    const preview = document.body.hasAttribute('data-preview');

    const navigate = useNavigate();

    const { meta: urls = [] } = useMeta('guestPageList');
    const { meta: images = [] } = useMeta('guestImages');

    const { setGallery } = useGallery();

    return (
        <>
            <ComposableMap
                projection="geoAlbers"
                projectionConfig={projectionConfig}
                viewBox={viewBox}
                style={{
                    maxHeight: height,
                    margin: '0 auto',
                    outline: 'none',
                }}
                data-tip=""
                {...rest}
            >
                <g
                    ref={svgRef}
                    key={`${groups?.length}_${hidden?.length}_${states?.length}`}
                >
                    <Geographies geography={data}>
                        {({ geographies }) =>
                            geographies.map((geo) => {
                                const page = urls.find(
                                    (el) => el.uuid === geo.properties.hrefUuid
                                );

                                return (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        fill={geo.properties.color}
                                        onMouseEnter={
                                            preview
                                                ? () => undefined
                                                : () => {
                                                      setTooltipContent(
                                                          geo.properties.name
                                                      );
                                                  }
                                        }
                                        onMouseLeave={
                                            preview
                                                ? () => undefined
                                                : () => {
                                                      setTooltipContent('');
                                                  }
                                        }
                                        className={
                                            page && !preview
                                                ? 'hasHref'
                                                : undefined
                                        }
                                        onClick={
                                            page && !preview
                                                ? () => navigate(page.href)
                                                : undefined
                                        }
                                    />
                                );
                            })
                        }
                    </Geographies>
                    {markers?.map((el) => {
                        const image = images.find(
                            (i) => i.uuid === el.imageUuid
                        );
                        const page = urls.find((p) => p.uuid === el.hrefUuid);
                        return (
                            <Marker
                                coordinates={el.coords}
                                key={el.id}
                                onClick={() => {
                                    if (image && !preview) {
                                        setGallery({
                                            src: image.file,
                                            alt: el.name,
                                        });
                                    } else if (page && !preview) {
                                        navigate(page.href);
                                    }
                                }}
                                onMouseEnter={
                                    preview
                                        ? () => undefined
                                        : () => {
                                              setTooltipContent(el.name || '');
                                          }
                                }
                                onMouseLeave={
                                    preview
                                        ? () => undefined
                                        : () => {
                                              setTooltipContent('');
                                          }
                                }
                            >
                                <MarkerIcon
                                    color={el.color}
                                    image={el.imageUuid}
                                    link={el.hrefUuid}
                                    scale={
                                        markerScale
                                            ? markerScale * scaleFactor
                                            : 0.7 * scaleFactor
                                    }
                                />
                            </Marker>
                        );
                    })}
                </g>
            </ComposableMap>
            <ReactTooltip
                className="react-tooltip"
                backgroundColor="white"
                arrowColor="white"
                textColor="inherit"
            >
                {tooltipContent}
            </ReactTooltip>
        </>
    );
};
